/**
 * The step between a binder and the roll.
 *
 * A sticker says a whole dollar, so something decides which way every price
 * goes. Left alone the app would round the ordinary way and never mention it,
 * and a vendor's margin lives in that decision: two hundred cards rounded down
 * is real money left behind, rounded up is a table of prices that all look
 * slightly grabby. So it is theirs, per binder and per card, with both totals
 * on screen.
 *
 * It is also where missing prices get filled. A card with no price cannot say
 * anything useful on a sticker, and finding that out one label at a time is the
 * failure this prevents.
 *
 * One button, and it says the next thing to do: fix the prices, connect the
 * printer, or print. The line above it always says where things stand, which is
 * what makes it safe for the button never to ask.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Printer, ArrowUp, ArrowDown, Check, X, Loader2 } from 'lucide-react';

import { useHideNavWhile } from '../BottomSheet';
import haptics from '../../utils/haptics';
import { useTheme } from '../../context/ThemeContext';
import {
  ROUNDING_MODES, DEFAULT_MODE, roundAll, readMode, writeMode,
} from '../../services/labelPrinter/rounding';

const hasPrice = (c) => !(c?.price === null || c?.price === undefined || c?.price === '');
const dollars = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const cents = (n) => `$${Number(n).toFixed(2)}`;

const NOTES = {
  auto: 'Standard rounding. A price ending .50 or above goes up, everything else goes down.',
  up: 'Every price to the next dollar. Nothing on the table sells for less than it is worth.',
  down: 'Every price to the dollar below. Rounder numbers, quicker sales, a little left on each.',
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {object[]} props.cards        in the order they will print
 * @param {string} props.binderName
 * @param {function} props.onSettings   opens the print settings sheet
 * @param {function} props.onPrint      (cardsWithRoundedPrices) => Promise
 * @param {function} [props.onPriceSaved] (cardId, price) - a price typed here is a price
 * @param {string|null} props.printerName
 * @param {boolean} props.linked
 * @param {boolean} props.linking
 * @param {number} props.printedCount
 * @param {boolean} props.printing
 * @param {string|null} props.error
 */
export default function RoundPricesSheet({
  open, onClose, cards = [], binderName,
  onSettings, onPrint, onPriceSaved,
  printerName, linked = false, linking = false,
  printedCount = 0, printing = false, error = null,
}) {
  const { theme } = useTheme();
  const [mode, setMode] = useState(DEFAULT_MODE);
  const [remember, setRemember] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [local, setLocal] = useState({});   // { cardId: typed price }
  const [fixing, setFixing] = useState(false);
  // The rows this pass is about, fixed on entry. A card priced during the pass
  // keeps its place, so the list under somebody's thumb never reorders and a
  // mistyped price never takes its own row away mid-correction.
  const fixIds = useRef([]);

  // The nav is painted by the shell, and on the phone the shell sits over a
  // sheet whatever the z-index says. This sheet ends in a button, so the nav
  // landing on top of it is the difference between printing and not.
  useHideNavWhile(open);

  useEffect(() => { if (open) setMode(readMode()); }, [open]);

  const priced = useMemo(
    () => cards.map((c) => (local[c.id] !== undefined ? { ...c, price: local[c.id] } : c)),
    [cards, local],
  );
  const gaps = useMemo(() => priced.filter((c) => !hasPrice(c)), [priced]);
  const { rows, real, printed, delta } = useMemo(
    () => roundAll(priced, mode, overrides),
    [priced, mode, overrides],
  );

  const setPrice = useCallback((card, raw) => {
    const v = parseFloat(raw);
    const value = Number.isFinite(v) ? v : null;
    setLocal((p) => ({ ...p, [card.id]: value }));
    if (value !== null) onPriceSaved?.(card.id, value);
  }, [onPriceSaved]);

  const flip = useCallback((row) => {
    haptics.tap();
    setOverrides((p) => ({ ...p, [row.card.id]: row.dir === 'up' ? 'down' : 'up' }));
  }, []);

  const pickMode = useCallback((key) => {
    haptics.tap();
    setMode(key);
    if (remember) writeMode(key);
  }, [remember]);

  const enterFixing = useCallback(() => {
    fixIds.current = gaps.map((c) => c.id);
    setFixing(true);
  }, [gaps]);

  const leaveFixing = useCallback(() => { setFixing(false); fixIds.current = []; }, []);

  const cta = useCallback(() => {
    if (printing || linking) return;
    if (fixing) return leaveFixing();
    if (gaps.length) return enterFixing();
    haptics.tap();
    onPrint(rows.map((r) => ({ ...r.card, price: r.value })));
  }, [printing, linking, fixing, gaps.length, rows, onPrint, enterFixing, leaveFixing]);

  if (!open) return null;
  const s = styles(theme);

  const visible = fixing ? rows.filter((r) => fixIds.current.includes(r.card.id)) : rows;

  let ctaText;
  let status;
  if (fixing) {
    ctaText = gaps.length ? `Skip the rest (${gaps.length})` : 'Done';
    status = gaps.length ? `${gaps.length} left` : 'All priced';
  } else if (gaps.length) {
    ctaText = `Fix pricing (${gaps.length})`;
    status = `${gaps.length} ${gaps.length === 1 ? 'card has' : 'cards have'} no price`;
  } else if (printing) {
    ctaText = `Printing ${printedCount} of ${cards.length}`;
    status = printerName || 'Printing';
  } else if (linking) {
    ctaText = 'Connecting';
    status = `Waking ${printerName || 'the printer'}`;
  } else if (error) {
    ctaText = 'Try again';
    status = error;
  } else if (linked) {
    ctaText = `Print ${cards.length} ${cards.length === 1 ? 'label' : 'labels'}`;
    status = printerName || 'Connected';
  } else {
    ctaText = `Connect and print ${cards.length}`;
    status = `${printerName || 'Printer'} not connected`;
  }

  return (
    <div style={s.scrim} onClick={fixing ? undefined : onClose} role="presentation">
      <div style={s.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Round before you print">
        <div style={s.scroll}>
          <div style={s.head}>
            <button style={s.back} onClick={fixing ? leaveFixing : onClose}>
              {fixing ? 'Back to prices' : (binderName || 'Back')}
            </button>
            <button style={s.close} onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>

          <h2 style={s.title}>{fixing ? 'Fix pricing' : 'Round before you print'}</h2>
          <p style={s.sub}>
            {fixing
              ? `${gaps.length} ${gaps.length === 1 ? 'card has' : 'cards have'} no price. Type one, or leave it and the sticker carries the name alone.`
              : 'A sticker says a whole dollar. Decide which way each one goes.'}
          </p>

          {!fixing && (
            <>
              <div style={s.modes}>
                {ROUNDING_MODES.map((m) => (
                  <button
                    key={m.key}
                    style={{ ...s.mode, ...(m.key === mode ? s.modeOn : null) }}
                    aria-pressed={m.key === mode}
                    onClick={() => pickMode(m.key)}
                  >
                    <span style={s.modeLabel}>{m.label}</span>
                    <span style={s.modeHint}>{m.hint}</span>
                  </button>
                ))}
              </div>
              <p style={s.note}>{NOTES[mode]}</p>

              <button
                style={s.remember}
                aria-pressed={remember}
                onClick={() => {
                  const next = !remember;
                  setRemember(next);
                  writeMode(next ? mode : null);
                }}
              >
                <span style={{ ...s.tick, ...(remember ? s.tickOn : null) }}>
                  {remember && <Check size={11} color="#06210f" strokeWidth={3.4} />}
                </span>
                Use this every time
              </button>

              <div style={s.totals}>
                <div style={s.total}>
                  <b style={s.totalValue}>{cents(real)}</b>
                  <span style={s.totalLabel}>Real total</span>
                </div>
                <div style={s.total}>
                  <b style={s.totalValue}>{dollars(printed)}</b>
                  <span style={s.totalLabel}>On the stickers</span>
                </div>
                <div style={s.total}>
                  <b style={{ ...s.totalValue, color: '#e6b45a' }}>
                    {delta >= 0 ? '+' : '−'}{cents(Math.abs(delta)).slice(1)}
                  </b>
                  <span style={s.totalLabel}>Difference</span>
                </div>
              </div>
            </>
          )}

          <div style={s.list}>
            {visible.map((r) => {
              const isGap = !hasPrice(r.card);
              return (
                <div key={r.card.id} style={{ ...s.row, ...(isGap ? s.rowGap : null) }}>
                  <div style={s.rowName}>
                    <span style={s.cardName}>{r.card.name}</span>
                    <span style={s.cardMeta}>{r.card.grade || ''}</span>
                  </div>

                  {fixing ? (
                    <>
                      <span style={isGap ? s.none : s.setNow}>
                        {isGap ? 'none' : dollars(r.value)}
                      </span>
                      <input
                        style={{ ...s.input, ...(isGap ? null : s.inputFilled) }}
                        inputMode="decimal"
                        placeholder="$"
                        defaultValue={hasPrice(r.card) ? String(r.card.price) : ''}
                        onBlur={(e) => setPrice(r.card, e.target.value)}
                      />
                    </>
                  ) : (
                    <>
                      <div style={s.prices}>
                        {isGap ? (
                          <span style={s.none}>none</span>
                        ) : (
                          <>
                            <span style={s.raw}>{cents(r.card.price)}</span>
                            <span style={s.arrow}>&rarr;</span>
                            <span style={{
                              ...s.final,
                              ...(r.dir === 'up' ? s.up : null),
                              ...(r.dir === 'down' ? s.down : null),
                            }}>
                              {dollars(r.value)}
                            </span>
                          </>
                        )}
                      </div>
                      {r.dir ? (
                        <button style={s.flip} onClick={() => flip(r)} aria-label="Flip this one">
                          {r.dir === 'up' ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
                        </button>
                      ) : <span style={{ width: 30, flex: 'none' }} />}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Pinned, because a binder can hold six hundred cards and Print cannot
            be at the bottom of six hundred rows. */}
        <div style={s.dock}>
          <div style={s.status}>
            <span style={{
              ...s.dot,
              ...(linked && !gaps.length ? s.dotOn : null),
              ...(linking || printing || gaps.length ? s.dotBusy : null),
            }} />
            {status}
          </div>
          <div style={s.dockRow}>
            {!fixing && (
              <button style={s.settings} onClick={onSettings}>
                <Printer size={15} /> Print settings
              </button>
            )}
            <button
              style={{ ...s.cta, ...(printing || linking ? s.ctaOff : null) }}
              disabled={printing || linking}
              onClick={cta}
            >
              {(printing || linking) && <Loader2 size={15} style={s.spin} />}
              {ctaText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = (theme) => ({
  scrim: {
    position: 'fixed', inset: 0, zIndex: 100040, background: 'rgba(0,0,0,.72)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  sheet: {
    width: '100%', maxWidth: 480, height: '92vh',
    display: 'flex', flexDirection: 'column',
    background: theme.bgSheet || '#101114',
    border: `0.5px solid ${theme.border}`, borderRadius: '18px 18px 0 0',
  },
  scroll: { flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '14px 16px 8px' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  back: {
    border: 0, background: 'transparent', color: theme.textSecondary,
    fontFamily: 'inherit', fontSize: 13, cursor: 'pointer', padding: 0,
  },
  close: {
    width: 30, height: 30, borderRadius: 15, border: `1px solid ${theme.border}`,
    background: 'transparent', color: theme.textSecondary, display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  title: { fontSize: 23, fontWeight: 700, margin: '0 0 3px', color: theme.text, letterSpacing: '-0.01em' },
  sub: { fontSize: 13, color: theme.textSecondary, margin: '0 0 16px', lineHeight: 1.45 },

  modes: { display: 'flex', gap: 7 },
  mode: {
    flex: 1, padding: '10px 6px', borderRadius: 10, border: `1px solid ${theme.border}`,
    background: theme.bgCard, color: theme.textSecondary, cursor: 'pointer',
    fontFamily: 'inherit', textAlign: 'center',
  },
  modeOn: { border: '1px solid #4ade80', background: 'rgba(74,222,128,.13)', color: '#4ade80' },
  modeLabel: { display: 'block', fontSize: 13, fontWeight: 600 },
  modeHint: { display: 'block', fontSize: 10, opacity: 0.75, marginTop: 1 },
  note: { fontSize: 11.5, color: theme.textMuted, margin: '8px 2px 0', lineHeight: 1.45 },

  remember: {
    display: 'flex', alignItems: 'center', gap: 7, margin: '10px 0 0', padding: 0,
    border: 0, background: 'transparent', color: theme.textMuted,
    fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  tick: {
    width: 15, height: 15, borderRadius: 5, border: `1px solid ${theme.border}`,
    flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  tickOn: { background: '#4ade80', border: '1px solid #4ade80' },

  totals: {
    display: 'flex', gap: 1, background: theme.border, border: `1px solid ${theme.border}`,
    borderRadius: 11, overflow: 'hidden', margin: '16px 0 0',
  },
  total: { flex: 1, background: theme.bgCard, padding: '11px 12px' },
  totalValue: { display: 'block', fontSize: 17, fontWeight: 600, color: theme.text, fontVariantNumeric: 'tabular-nums' },
  totalLabel: { display: 'block', fontSize: 10.5, color: theme.textMuted, marginTop: 2 },

  list: { margin: '14px 0 0', border: `1px solid ${theme.border}`, borderRadius: 12, overflow: 'hidden' },
  row: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    borderBottom: `1px solid ${theme.border}`, background: theme.bgCard,
  },
  rowGap: { background: 'rgba(230,180,90,.07)' },
  rowName: { flex: 1, minWidth: 0 },
  cardName: {
    display: 'block', fontSize: 14, fontWeight: 600, color: theme.text,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  cardMeta: { display: 'block', fontSize: 11, color: theme.textMuted, marginTop: 1 },
  prices: { display: 'flex', alignItems: 'baseline', gap: 7, flex: 'none', fontVariantNumeric: 'tabular-nums' },
  raw: { fontSize: 12.5, color: theme.textMuted },
  arrow: { fontSize: 11, color: theme.textMuted },
  final: { fontSize: 16, fontWeight: 600, color: theme.text, minWidth: 52, textAlign: 'right' },
  up: { color: '#4ade80' },
  down: { color: '#5aa9e6' },
  none: { fontSize: 11, color: theme.textMuted, flex: 'none' },
  setNow: { fontSize: 12, color: '#4ade80', fontWeight: 600, flex: 'none' },
  flip: {
    flex: 'none', width: 30, height: 30, borderRadius: 9, border: `1px solid ${theme.border}`,
    background: 'transparent', color: theme.textSecondary, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  input: {
    flex: 'none', width: 86, padding: '7px 9px', borderRadius: 9,
    border: '1px solid rgba(230,180,90,.45)', background: theme.bgCard, color: theme.text,
    fontFamily: 'inherit', fontSize: 13.5, textAlign: 'right',
  },
  inputFilled: { border: '1px solid rgba(74,222,128,.4)' },

  dock: {
    flex: '0 0 auto', padding: '12px 16px calc(14px + var(--safe-area-bottom, 0px))',
    borderTop: `1px solid ${theme.border}`, background: theme.bgSheet || '#101114',
  },
  status: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
    color: theme.textMuted, marginBottom: 9, lineHeight: 1.4,
  },
  dot: { width: 7, height: 7, borderRadius: 4, flex: 'none', background: theme.textMuted },
  dotOn: { background: '#4ade80' },
  dotBusy: { background: '#e6b45a' },
  dockRow: { display: 'flex', gap: 9, alignItems: 'center' },
  settings: {
    flex: 'none', display: 'flex', alignItems: 'center', gap: 7, padding: '14px',
    borderRadius: 11, border: `1px solid ${theme.border}`, background: 'transparent',
    color: theme.textSecondary, fontFamily: 'inherit', fontSize: 13.5,
    fontWeight: 600, cursor: 'pointer',
  },
  cta: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: 14, borderRadius: 11, border: 0, background: '#4ade80', color: '#06210f',
    fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  ctaOff: { opacity: 0.55, cursor: 'default' },
  spin: { animation: 'cc-spin 1s linear infinite' },
});
